/**
 * Multi-line text primitive — the renderer for the generative-UI `textarea`
 * form field (`UiFormField` kind `'textarea'` in `@4f/shared`), which until now
 * had no component and fell back to a single-line input.
 *
 * Same contract as {@link TextInput}: controlled (`value` + `onValue`),
 * token-driven, optional visible label, `aria-invalid`, shared focus ring.
 * Optional `maxLength` renders a live counter that is announced politely rather
 * than on every keystroke.
 */
import type { JSX } from 'solid-js';
import { createUniqueId, Show, splitProps } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-textarea';
const CSS = `
.f4-textarea-field { display: flex; flex-direction: column; gap: var(--space-1); }
.f4-textarea-field__label { font-size: var(--font-size-sm); font-weight: var(--font-weight-semibold); color: var(--color-text); }
.f4-textarea {
  --f4-focus-offset: 1px;
  width: 100%;
  min-height: var(--space-16);
  padding: var(--space-2) var(--space-3);
  font: inherit;
  line-height: var(--line-height-normal);
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  resize: vertical;
  transition: border-color var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-textarea::placeholder { color: var(--color-text-subtle); }
.f4-textarea:focus-visible { border-color: var(--color-accent); }
.f4-textarea[aria-invalid="true"] { border-color: var(--color-danger); }
.f4-textarea[disabled] { opacity: 0.6; cursor: not-allowed; background: var(--color-surface-sunken); }
.f4-textarea-field__count {
  align-self: flex-end;
  font-size: var(--font-size-xs);
  color: var(--color-text-subtle);
}
.f4-textarea-field__count--over { color: var(--color-danger); }
@media (prefers-reduced-motion: reduce) { .f4-textarea { transition: none; } }
`;

export interface TextareaProps
  extends Omit<JSX.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onInput' | 'value'> {
  /** Current value. */
  readonly value: string;
  /** Emitted with the new value on every input. */
  readonly onValue: (value: string) => void;
  /** Visible label text. Rendered as a `<label>` when provided. */
  readonly label?: string;
  /** Element id — links the label. Auto-generated when a `label` is set but no id is passed. */
  readonly id?: string;
  /** Mark the field invalid (`aria-invalid`). */
  readonly invalid?: boolean;
  /** Visible rows. Defaults to 4. */
  readonly rows?: number;
  /**
   * Character budget. When set, a `used / max` counter renders and the field is
   * marked invalid once the budget is exceeded. Does NOT truncate — the caller
   * decides what an over-budget value means.
   */
  readonly maxLength?: number;
  readonly class?: string;
}

export function Textarea(props: TextareaProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  const [local, rest] = splitProps(props, [
    'value',
    'onValue',
    'label',
    'id',
    'invalid',
    'rows',
    'maxLength',
    'class',
  ]);

  // A visible label must point at a real control; generate an id when the
  // caller gives a label but no id, so the association is never dropped.
  const generatedId = createUniqueId();
  const fieldId = (): string | undefined =>
    local.id ?? (local.label !== undefined ? generatedId : undefined);
  const countId = (): string => `${fieldId() ?? generatedId}-count`;

  const over = (): boolean => local.maxLength !== undefined && local.value.length > local.maxLength;
  const invalid = (): boolean => local.invalid === true || over();

  return (
    <div class="f4-textarea-field">
      <Show when={local.label}>
        <label class="f4-textarea-field__label" for={fieldId()}>
          {local.label}
        </label>
      </Show>
      <textarea
        {...rest}
        id={fieldId()}
        rows={local.rows ?? 4}
        class={cx('f4-textarea', 'f4-focus-ring', local.class)}
        value={local.value}
        aria-invalid={invalid() ? 'true' : undefined}
        aria-describedby={local.maxLength === undefined ? undefined : countId()}
        onInput={(event) => local.onValue(event.currentTarget.value)}
      />
      <Show when={local.maxLength !== undefined}>
        <span
          id={countId()}
          class={cx('f4-textarea-field__count', over() && 'f4-textarea-field__count--over')}
          // Polite + atomic: announced when the user pauses, not per keystroke.
          aria-live="polite"
          aria-atomic="true"
        >
          {`${local.value.length}/${local.maxLength}`}
        </span>
      </Show>
    </div>
  );
}
